import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Button,
  Divider,
  Dialog,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Skeleton,
  IconButton,
  Tooltip,
  DialogTitle,
} from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import AccountBalanceOutlinedIcon from '@mui/icons-material/AccountBalanceOutlined';
import PhoneAndroidOutlinedIcon from '@mui/icons-material/PhoneAndroidOutlined';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import InboxOutlinedIcon from '@mui/icons-material/InboxOutlined';
import AddBankAccountModal from '../components/AddBankAccount';
import EditBankAccountModal from '../components/EditBankAccountModal';
import { getAllBankAccounts, getBankAccountById, deleteBankAccount } from '../api/services';

// Shared look for the pages (design tokens + small UI helpers)
const fin = {
  primary: '#009db9',
  primaryDark: '#007a8a',
  primarySoft: '#e6f6f9',
  text: '#1f2933',
  muted: '#6b7280',
  border: '#e5e7eb',
  surface: '#f7f9fb',
  success: '#2e7d32',
  successSoft: '#e8f5e9',
  danger: '#d32f2f',
  dangerSoft: '#ffebee',
  warning: '#b26a00',
  warningSoft: '#fff4e5',
  radius: '16px',
  shadow: '0 4px 20px rgba(16, 24, 40, 0.06)',
};

const primaryButtonSx = {
  backgroundColor: fin.primary,
  color: '#fff',
  borderRadius: '10px',
  textTransform: 'none',
  fontWeight: 700,
  px: 3,
  py: 1.25,
  boxShadow: 'none',
  '&:hover': { backgroundColor: fin.primaryDark, boxShadow: 'none' },
  '&.Mui-disabled': { backgroundColor: '#d5dbe0', color: '#8a949e' },
};

const headCellSx = {
  fontWeight: 700,
  color: fin.primaryDark,
  backgroundColor: fin.primarySoft,
  fontSize: '0.8rem',
  py: 1.5,
  borderBottom: `1px solid ${fin.border}`,
};

const bodyCellSx = {
  color: fin.text,
  py: 1.75,
  fontSize: '0.875rem',
  borderBottom: `1px solid ${fin.border}`,
};

const mobileCardSx = {
  border: `1px solid ${fin.border}`,
  borderRadius: '12px',
  p: 2,
  backgroundColor: '#fff',
};

const PageHeader = ({ title }) => (
  <Box sx={{ mb: { xs: 3, md: 4 } }}>
    <Typography
      variant="h4"
      sx={{
        fontWeight: 'bold',
        color: fin.text,
        letterSpacing: 0.5,
        fontSize: { xs: '1.5rem', sm: '1.85rem', md: '2.125rem' },
        position: 'relative',
        '&::after': {
          content: '""',
          display: 'block',
          backgroundColor: fin.primary,
          height: '4px',
          width: { xs: '80px', sm: '100px' },
          position: 'absolute',
          left: 0,
          bottom: '-8px',
        },
      }}
    >
      {title}
    </Typography>
  </Box>
);

const EmptyState = ({ icon: Icon = InboxOutlinedIcon, title, message, action }) => (
  <Box sx={{ textAlign: 'center', py: { xs: 5, md: 7 }, px: 2 }}>
    <Box
      sx={{
        width: 64,
        height: 64,
        mx: 'auto',
        mb: 2,
        borderRadius: '50%',
        backgroundColor: fin.primarySoft,
        color: fin.primary,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Icon sx={{ fontSize: 32 }} />
    </Box>
    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: fin.text }}>
      {title}
    </Typography>
    {message && (
      <Typography
        variant="body2"
        sx={{ color: fin.muted, mt: 0.5, maxWidth: 360, mx: 'auto' }}
      >
        {message}
      </Typography>
    )}
    {action && <Box sx={{ mt: 2.5 }}>{action}</Box>}
  </Box>
);

const ListSkeleton = ({ rows = 5 }) => (
  <Box
    role="status"
    aria-label="Loading"
    sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}
  >
    {Array.from({ length: rows }).map((_, i) => (
      <Skeleton key={i} variant="rounded" height={56} animation="wave" />
    ))}
  </Box>
);

const DialogHeading = ({ icon: Icon, tone = 'primary', children }) => (
  <DialogTitle
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 1.5,
      fontWeight: 700,
      color: fin.text,
      pb: 1.5,
    }}
  >
    <Box
      sx={{
        flexShrink: 0,
        width: 36,
        height: 36,
        borderRadius: '10px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: tone === 'danger' ? fin.dangerSoft : fin.primarySoft,
        color: tone === 'danger' ? fin.danger : fin.primary,
      }}
    >
      <Icon fontSize="small" />
    </Box>
    {children}
  </DialogTitle>
);

const dialogPaperProps = { sx: { borderRadius: '16px', m: { xs: 2, sm: 4 } } };

const cancelButtonSx = {
  color: fin.muted,
  textTransform: 'none',
  fontWeight: 600,
};

const confirmButtonSx = (danger) => ({
  ...primaryButtonSx,
  py: 1,
  ...(danger
    ? { backgroundColor: fin.danger, '&:hover': { backgroundColor: '#b71c1c' } }
    : {}),
});

const actionTones = {
  primary: { color: fin.primary, soft: fin.primarySoft },
  success: { color: fin.success, soft: fin.successSoft },
  danger: { color: fin.danger, soft: fin.dangerSoft },
};

const ActionButton = ({ tone = 'primary', icon: Icon, children, sx, ...props }) => {
  const a = actionTones[tone] || actionTones.primary;
  return (
    <Button
      variant="outlined"
      size="small"
      startIcon={Icon ? <Icon fontSize="small" /> : null}
      sx={{
        textTransform: 'none',
        fontWeight: 600,
        borderRadius: '8px',
        color: a.color,
        borderColor: a.color,
        '&:hover': { borderColor: a.color, backgroundColor: a.soft },
        '&.Mui-disabled': { color: '#b6bec6', borderColor: '#dfe3e8' },
        ...sx,
      }}
      {...props}
    >
      {children}
    </Button>
  );
};

const ActionIcon = ({ title, tone = 'primary', icon: Icon, ...props }) => {
  const a = actionTones[tone] || actionTones.primary;
  return (
    <Tooltip title={title}>
      <span>
        <IconButton
          size="small"
          aria-label={title}
          sx={{
            color: a.color,
            '&:hover': { backgroundColor: a.soft },
            '&.Mui-disabled': { color: '#b6bec6' },
          }}
          {...props}
        >
          <Icon fontSize="small" />
        </IconButton>
      </span>
    </Tooltip>
  );
};

const tableWrapSx = (bp) => ({
  display: { xs: 'none', [bp]: 'block' },
  overflowX: 'auto',
  border: `1px solid ${fin.border}`,
  borderRadius: '12px',
});

const cardListSx = (bp) => ({
  display: { xs: 'flex', [bp]: 'none' },
  flexDirection: 'column',
  gap: 1.5,
});


const monoSx = {
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
  letterSpacing: 0.5,
};

const DirectDepositPage = () => {
  const [openAddModal, setOpenAddModal] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleOpenAddModal = () => setOpenAddModal(true);
  const handleCloseAddModal = () => setOpenAddModal(false);

  const handleOpenEditModal = async (account) => {
    setEditLoading(true);
    setError(null);
    try {
      const latestAccount = await getBankAccountById(account.id);
      setSelectedAccount(latestAccount);
      setOpenEditModal(true);
    } catch (err) {
      setError('Failed to fetch account details. Please try again.');
    } finally {
      setEditLoading(false);
    }
  };

  const handleCloseEditModal = () => {
    setSelectedAccount(null);
    setOpenEditModal(false);
  };

  const handleOpenDeleteDialog = (account) => {
    setSelectedAccount(account);
    setOpenDeleteDialog(true);
  };
  const handleCloseDeleteDialog = () => {
    setSelectedAccount(null);
    setOpenDeleteDialog(false);
  };

  const canAddMoreAccounts = () => {
    const bankAccountsCount = accounts.filter(account => 
      !account.mobileNumber || account.mobileNumber.trim() === ''
    ).length;
    
    const mobileMoneyCount = accounts.filter(account => 
      account.mobileNumber && account.mobileNumber.trim() !== ''
    ).length;

    return bankAccountsCount < 1 || mobileMoneyCount < 1;
  };

  const hasBothAccountTypes = () => {
    const hasBankAccount = accounts.some(account => 
      !account.mobileNumber || account.mobileNumber.trim() === ''
    );
    
    const hasMobileMoney = accounts.some(account => 
      account.mobileNumber && account.mobileNumber.trim() !== ''
    );

    return hasBankAccount && hasMobileMoney;
  };

  const maskAccountNumber = (number) => {
    if (!number) return 'N/A';
    const lastFour = number.slice(-4);
    return `****${lastFour}`;
  };

  const maskMobileNumber = (number) => {
    if (!number) return 'N/A';
    const countryCode = number.startsWith('+') ? number.split(' ')[0] : '';
    const mainNumber = number.replace(countryCode, '').trim();
    if (mainNumber.length <= 4) return number;
    
    const visiblePart = mainNumber.slice(-2);
    const maskedPart = '*'.repeat(mainNumber.length - 2);
    return `${countryCode} ${maskedPart}${visiblePart}`;
  };

  const getAccountDisplayData = (account) => {
    const hasMobileNumber = account.mobileNumber && account.mobileNumber.trim() !== '';
    
    if (hasMobileNumber) {
      return {
        type: 'Mobile Money',
        institution: account.bankName || 'Mobile Money',
        accountInfo: maskMobileNumber(account.mobileNumber),
        currency: account.accountCurrency || 'N/A',
        displayType: 'Mobile Money'
      };
    } else {
      return {
        type: account.accountType || 'Bank Account',
        institution: account.bankName || 'N/A',
        accountInfo: maskAccountNumber(account.accountNumber),
        currency: account.accountCurrency || 'N/A',
        displayType: 'Bank Account'
      };
    }
  };

  const fetchBankAccounts = async () => {
    setLoading(true);
    setError(null);
    try {
      const bankAccounts = await getAllBankAccounts();
      if (!Array.isArray(bankAccounts)) {
        console.error('Expected an array but got:', bankAccounts);
        setAccounts([]);
      } else {
        setAccounts(bankAccounts);
      }
    } catch (err) {
      if (err.response && err.response.status === 404) {
        setAccounts([]);
      } else {
        setError('Failed to fetch accounts. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedAccount) return;
    setLoading(true);
    setError(null);
    try {
      await deleteBankAccount(selectedAccount.id);
      await fetchBankAccounts();
      handleCloseDeleteDialog();
    } catch (err) {
      setError('Failed to delete account. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBankAccounts();
  }, []);

  const showAddButton = canAddMoreAccounts();
  const hasMaxAccounts = hasBothAccountTypes();

  const typeIcon = (displayType) =>
    displayType === 'Mobile Money' ? PhoneAndroidOutlinedIcon : AccountBalanceOutlinedIcon;

  const renderActions = (account, withLabels) =>
    withLabels ? (
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 1 }}>
        <ActionButton icon={EditOutlinedIcon} onClick={() => handleOpenEditModal(account)} disabled={editLoading}>
          Edit
        </ActionButton>
        <ActionButton tone="danger" icon={DeleteOutlineIcon} onClick={() => handleOpenDeleteDialog(account)}>
          Delete
        </ActionButton>
      </Box>
    ) : (
      <Box sx={{ display: 'flex', gap: 0.5 }}>
        <ActionIcon title="Edit" icon={EditOutlinedIcon} onClick={() => handleOpenEditModal(account)} disabled={editLoading} />
        <ActionIcon title="Delete" tone="danger" icon={DeleteOutlineIcon} onClick={() => handleOpenDeleteDialog(account)} />
      </Box>
    );

  return (
    <>
      <Box sx={{ 
        minHeight: '100vh', 
        bgcolor: '#fff', 
        display: 'flex', 
        flexDirection: 'column', 
        overflowX: 'hidden',
      }}>
        <Box sx={{ 
          flex: 1, 
          pt: 3,
          px: 2,
        }}>
          <Box sx={{ 
            width: '100%', 
            maxWidth: { xs: '100%', sm: '100%', md: '1200px' }, 
            mx: 'auto',
          }}>
            <Box sx={{ p: { xs: 1, sm: 2, md: 4 }, pt: { xs: 3, md: 5 }, width: '100%', boxSizing: 'border-box' }}>
              <PageHeader title="Payroll" />

              <Box
                sx={{
                  borderRadius: fin.radius,
                  border: `1px solid ${fin.border}`,
                  backgroundColor: '#fff',
                  boxShadow: fin.shadow,
                  p: { xs: 2, sm: 3, md: 4 },
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    justifyContent: 'space-between',
                    alignItems: { xs: 'stretch', sm: 'flex-start' },
                    gap: 2,
                    mb: 3,
                  }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      variant="h5"
                      sx={{ fontWeight: 700, color: fin.text, fontSize: { xs: '1.25rem', md: '1.5rem' } }}
                      data-tour="dd-page-title"
                    >
                      Direct Deposit
                    </Typography>
                    <Typography variant="body1" sx={{ color: fin.muted, mt: 0.5 }}>
                      Manage the direct deposit of your salary. You can add one bank account and one mobile money account.
                    </Typography>
                  </Box>
                  {showAddButton && (
                    <Button
                      variant="contained"
                      startIcon={<AddIcon />}
                      onClick={handleOpenAddModal}
                      data-tour="dd-add-account"
                      sx={{ ...primaryButtonSx, flexShrink: 0, alignSelf: { sm: 'flex-start' } }}
                    >
                      Add Account
                    </Button>
                  )}
                </Box>

                <Divider sx={{ mb: 3 }} />

                {error && (
                  <Alert severity="error" variant="filled" sx={{ mb: 3, borderRadius: '10px' }}>
                    {error}
                  </Alert>
                )}

                {loading ? (
                  <ListSkeleton rows={2} />
                ) : accounts.length === 0 ? (
                  <EmptyState
                    icon={AccountBalanceWalletOutlinedIcon}
                    title="No accounts added yet."
                    message="You can add one bank account and one mobile money account."
                  />
                ) : (
                  <>
                    <Box sx={tableWrapSx('md')}>
                      <Table sx={{ minWidth: 560 }}>
                        <TableHead>
                          <TableRow>
                            {['Type of Account', 'Institution Name', 'Account Number', 'Currency', 'Action'].map((header) => (
                              <TableCell key={header} sx={{ ...headCellSx, whiteSpace: 'nowrap' }}>
                                {header}
                              </TableCell>
                            ))}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {accounts.map((account, index) => {
                            const displayData = getAccountDisplayData(account);
                            const TypeIcon = typeIcon(displayData.displayType);
                            return (
                              <TableRow key={account.id || index} hover sx={{ '&:last-child td': { borderBottom: 0 } }}>
                                <TableCell sx={bodyCellSx}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                    <Box sx={{ width: 34, height: 34, borderRadius: '10px', backgroundColor: fin.primarySoft, color: fin.primary, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                      <TypeIcon fontSize="small" />
                                    </Box>
                                    <Typography sx={{ fontWeight: 600, fontSize: '0.875rem' }}>
                                      {displayData.displayType}
                                    </Typography>
                                  </Box>
                                </TableCell>
                                <TableCell sx={{ ...bodyCellSx, wordBreak: 'break-word' }}>{displayData.institution}</TableCell>
                                <TableCell sx={{ ...bodyCellSx, ...monoSx }}>{displayData.accountInfo}</TableCell>
                                <TableCell sx={bodyCellSx}>{displayData.currency}</TableCell>
                                <TableCell sx={bodyCellSx}>{renderActions(account, false)}</TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </Box>

                    <Box sx={cardListSx('md')}>
                      {accounts.map((account, index) => {
                        const displayData = getAccountDisplayData(account);
                        const TypeIcon = typeIcon(displayData.displayType);
                        return (
                          <Box key={account.id || index} sx={mobileCardSx}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                              <Box sx={{ width: 40, height: 40, borderRadius: '10px', backgroundColor: fin.primarySoft, color: fin.primary, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <TypeIcon />
                              </Box>
                              <Box sx={{ minWidth: 0 }}>
                                <Typography sx={{ fontWeight: 700, color: fin.text }}>{displayData.displayType}</Typography>
                                <Typography variant="body2" sx={{ color: fin.muted, wordBreak: 'break-word' }}>{displayData.institution}</Typography>
                              </Box>
                            </Box>
                            <Box sx={{ p: 1.5, mb: 1.5, borderRadius: '10px', backgroundColor: fin.surface, display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 1 }}>
                              <Box>
                                <Typography variant="caption" sx={{ color: fin.muted }}>Account Number</Typography>
                                <Typography sx={{ fontWeight: 600, fontSize: '0.9rem', ...monoSx }}>{displayData.accountInfo}</Typography>
                              </Box>
                              <Box>
                                <Typography variant="caption" sx={{ color: fin.muted }}>Currency</Typography>
                                <Typography sx={{ fontWeight: 600, fontSize: '0.9rem' }}>{displayData.currency}</Typography>
                              </Box>
                            </Box>
                            {renderActions(account, true)}
                          </Box>
                        );
                      })}
                    </Box>
                  </>
                )}

                {hasMaxAccounts && (
                  <Typography variant="body2" sx={{ color: fin.muted, mt: 2 }}>
                    You have reached the maximum number of accounts (one bank account and one mobile money account).
                  </Typography>
                )}

                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    mt: 3,
                    p: { xs: 1.5, sm: 2 },
                    backgroundColor: fin.surface,
                    borderRadius: '10px',
                    borderLeft: `4px solid ${fin.primary}`,
                  }}
                  data-tour="dd-info-note"
                >
                  <InfoOutlinedIcon sx={{ color: fin.primary }} />
                  <Typography variant="body2" sx={{ color: fin.text, flex: 1 }}>
                    If you change your account, it will apply only next month.
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* MODALS - RENDERED ONLY ONCE AT THE END */}
      <AddBankAccountModal 
        open={openAddModal} 
        onClose={handleCloseAddModal} 
        onSuccess={fetchBankAccounts}
        existingAccounts={accounts}
      />
      <EditBankAccountModal 
        open={openEditModal} 
        onClose={handleCloseEditModal} 
        onSuccess={fetchBankAccounts} 
        account={selectedAccount} 
      />
      <Dialog open={openDeleteDialog} onClose={handleCloseDeleteDialog} PaperProps={dialogPaperProps} fullWidth maxWidth="xs">
        <DialogHeading icon={WarningAmberRoundedIcon} tone="danger">Confirm Deletion</DialogHeading>
        <Divider />
        <DialogContent sx={{ pt: 2.5 }}>
          <Typography sx={{ color: fin.text }}>
            Are you sure you want to delete this {selectedAccount && getAccountDisplayData(selectedAccount).displayType.toLowerCase()}?
          </Typography>
        </DialogContent>
        <Divider />
        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
          <Button onClick={handleCloseDeleteDialog} sx={cancelButtonSx}>Cancel</Button>
          <Button
            onClick={handleDelete}
            disabled={loading}
            sx={confirmButtonSx(true)}
            startIcon={loading ? <CircularProgress size={16} sx={{ color: 'inherit' }} /> : null}
          >
            {loading ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default DirectDepositPage;